import { useState, useMemo } from 'react'
import { useTheme } from '@mui/material/styles'
import {
  ChatProvider,
  ChatWindow,
  ConversationList,
  requestTherapistToken,
  requestPatientToken,
  createConversation,
  extractTherapistId,
  extractPatientId,
} from '@/chat'
import type {
  ChatConversation,
  PatientInfo,
  TherapistInfo,
} from '@/chat/services/chatApi'
import { UserRole } from '@/types'
import { useAppSelector } from '@/store'

const getEntityDisplayName = (entity: unknown, fallback: string): string => {
  if (!entity || typeof entity === 'string') return fallback

  const typed = entity as PatientInfo | TherapistInfo | { name?: string }
  const first = (typed as PatientInfo | TherapistInfo).firstName?.trim() ?? ''
  const last = (typed as PatientInfo | TherapistInfo).lastName?.trim() ?? ''
  const name = (typed as { name?: string }).name?.trim() ?? ''

  const combined = `${first || name} ${last}`.trim()
  return combined || fallback
}

const getEntityAvatarUrl = (entity: unknown): string | undefined => {
  if (!entity || typeof entity === 'string') return undefined
  const typed = entity as
    | PatientInfo
    | TherapistInfo
    | {
        avatarUrl?: string
        avatar?: string
        profilePhotoUrl?: string
      }
  return (
    typed.profilePhotoUrl ??
    ('avatarUrl' in typed ? typed.avatarUrl : undefined) ??
    ('avatar' in typed ? typed.avatar : undefined)
  )
}

const getCurrentUserDisplayName = (user?: {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  id?: string | null
}): string => {
  if (!user) return 'You'
  const first = user.firstName?.trim() ?? ''
  const last = user.lastName?.trim() ?? ''
  const combined = `${first} ${last}`.trim()
  if (combined) return combined
  if (user.email) return user.email
  if (user.id) return user.id
  return 'You'
}

const MessagingPage = () => {
  const theme = useTheme()
  const { user } = useAppSelector(state => state.auth)
  const [selectedConversation, setSelectedConversation] =
    useState<ChatConversation | null>(null)

  const currentUserId = user?.id ?? ''
  const role = user?.role ?? null
  const isTherapist = role === UserRole.THERAPIST
  const participantFallbackLabel = isTherapist ? 'Patient' : 'Provider'

  const participantEntity = selectedConversation
    ? isTherapist
      ? selectedConversation.patientId
      : selectedConversation.therapistId
    : null

  const isParticipantDeleted = !participantEntity

  const participantDisplayName = isParticipantDeleted
    ? 'Deleted user'
    : getEntityDisplayName(participantEntity, participantFallbackLabel)

  const participantAvatarUrl = isParticipantDeleted
    ? undefined
    : getEntityAvatarUrl(participantEntity)

  const currentUserDisplayName = getCurrentUserDisplayName(user ?? undefined)

  const tokenFetcher = useMemo(() => {
    if (!currentUserId) return undefined
    return isTherapist
      ? (signal?: AbortSignal) => requestTherapistToken(currentUserId, signal)
      : (signal?: AbortSignal) => requestPatientToken(currentUserId, signal)
  }, [currentUserId, isTherapist])

  const conversationResolver = useMemo(() => {
    if (!selectedConversation || !currentUserId) return undefined

    const therapistId = extractTherapistId(selectedConversation.therapistId)
    const patientId = extractPatientId(selectedConversation.patientId)

    return async (signal?: AbortSignal): Promise<string> => {
      try {
        // Create or ensure the conversation exists in Twilio
        // This will either create a new one or return the existing one
        console.log('Creating conversation with:', { therapistId, patientId })
        const conversation = await createConversation(
          {
            therapistId,
            patientId,
          },
          signal
        )

        console.log('Create conversation response:', conversation)

        // Use conversationId as primary field (matches API response)
        const sid = conversation.conversationId ?? conversation.conversationSid
        if (!sid) {
          throw new Error(
            'Conversation SID missing from server response. Response: ' +
              JSON.stringify(conversation)
          )
        }

        // Validate SID format (Twilio conversation SIDs start with "CH")
        if (!sid.startsWith('CH')) {
          console.warn(
            'Warning: Conversation SID format may be invalid:',
            sid,
            'Expected format: CHxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'
          )
        }

        console.log('Create conversation response:', {
          conversationId: sid,
          reused: conversation.reused,
          fullResponse: conversation,
        })

        // If conversation was reused (already existed), it should be immediately available
        // If it was newly created, Twilio might need a moment to propagate it
        if (conversation.reused) {
          console.log(
            'Conversation was reused (already exists), should be immediately available'
          )
        } else {
          console.log(
            'Conversation was newly created, may need a moment to propagate in Twilio'
          )
        }

        return sid
      } catch (error) {
        console.error('Failed to create conversation:', error)
        throw new Error(
          `Failed to create conversation: ${
            error instanceof Error ? error.message : String(error)
          }`
        )
      }
    }
  }, [selectedConversation, currentUserId])

  const handleSelectConversation = (conversation: ChatConversation) => {
    setSelectedConversation(conversation)
  }

  if (!currentUserId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-slate-500">
        Please log in to start messaging.
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] w-full overflow-hidden bg-[#F6F7F9]">
      {/* Conversation List Sidebar */}
      <div className="flex flex-col w-1/3 h-full overflow-hidden bg-white border-r shadow-sm border-slate-200">
        <div
          className="flex-shrink-0 px-6 py-4 border-b shadow border-slate-100"
          style={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: theme.palette.primary.contrastText,
          }}
        >
          <h2 className="text-xl font-semibold">Messages</h2>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          <ConversationList
            onSelectConversation={handleSelectConversation}
            selectedConversationSid={
              selectedConversation?.conversationId ??
              selectedConversation?.conversationSid
            }
          />
        </div>
      </div>

      {/* Chat Window */}
      <div className="flex-1">
        {selectedConversation && tokenFetcher && conversationResolver ? (
          <ChatProvider
            key={`${
              selectedConversation.conversationId ??
              selectedConversation.conversationSid
            }-${Date.now()}`}
            config={{
              tokenFetcher,
              conversationResolver,
              enableDebugLogging: import.meta.env.DEV,
              // REST API configuration for sending messages
              useRestApiForMessages: true,
              restApiConversationId:
                selectedConversation.conversationId ??
                selectedConversation.conversationSid,
              restApiSenderRole: isTherapist ? 'therapist' : 'patient',
              restApiSenderId: currentUserId, // Always a string ID
              restApiRecipientId: isTherapist
                ? extractPatientId(selectedConversation.patientId)
                : extractTherapistId(selectedConversation.therapistId),
              // Enable chat history loading
              loadChatHistory: true,
              participantDisplayName:
                participantDisplayName ?? participantFallbackLabel,
              participantAvatarUrl: participantAvatarUrl,
              currentUserDisplayName,
            }}
          >
            <ChatWindow />
          </ChatProvider>
        ) : (
          <div className="flex items-center justify-center h-full bg-white">
            <div className="text-center">
              <p className="text-lg font-semibold text-slate-700">
                Select a conversation
              </p>
              <p className="mt-2 text-sm text-slate-500">
                Choose a conversation from the list to start messaging
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MessagingPage
